/**
 * arbi cite                    — list all citations from the last response
 * arbi cite <N>                — show citation N (markdown block with every field)
 * arbi cite -a / --all         — show every citation as markdown blocks
 * arbi cite --json             — JSON envelope, same data
 * arbi cite verify <N>         — verification block for citation N
 *
 * Reads persisted metadata from `~/.arbi/last-metadata.json`, written by
 * the last `arbi ask` response.
 *
 * ── Design note: one canonical format ───────────────────────────────────────
 *
 * The previous incarnation had three text shapes (default pretty, --raw text
 * dump, --json) and `arbi cite --json | jq …` was the only path to extract
 * a doc_id or chunk_ext_id without parsing fragments of the human output.
 *
 * That put formatting work on the agent. The new default IS the
 * agent-grade format: a markdown block per citation with every field
 * exposed under stable headings (`**Doc ID:** doc-…`, `**Chunk ID:** chk-…`,
 * `**SHA256:** …`) that an agent can extract with a one-line regex — no
 * external tool needed. Humans get the same blocks rendered the same way,
 * with chalk for the headers when stdout is a TTY.
 *
 * `--json` still exists for the rare case where an agent really wants the
 * full structured payload, but it's no longer the *only* path to the
 * underlying IDs. `--raw` is gone — its job (just the passage text) is
 * trivially recovered by extracting the fenced code block from the
 * canonical output.
 */

import { createHash } from 'node:crypto'
import { Command } from 'commander'
import { resolveCitations, countCitations, type MessageMetadataPayload } from '@arbidocs/sdk'
import { runAction, printJson } from '../helpers.js'
import { loadLastMetadata } from '../config.js'
import { chalk } from '../output.js'

function hashUtf8(s: string): string {
  return createHash('sha256').update(s, 'utf8').digest('hex')
}

type Resolved = ReturnType<typeof resolveCitations>[number]

export function registerCiteCommand(program: Command): void {
  const cite = program
    .command('cite [number]')
    .description('Browse citations from the last `arbi ask` response in this shell')
    .allowExcessArguments(true)
    .option('-a, --all', 'Show all citations as markdown blocks')
    .option('--json', 'Output citations as JSON (full structured payload)')
    .action((numberOrVerb: string | undefined, opts: { all?: boolean; json?: boolean }) =>
      runAction(async () => {
        if (numberOrVerb === 'verify') {
          const argv = process.argv
          const i = argv.indexOf('verify')
          const target = i !== -1 ? argv[i + 1] : undefined
          if (!target) {
            console.error('Usage: arbi cite verify <number>')
            process.exit(1)
          }
          await verifyOne(target, opts)
          return
        }
        await listOrShow(numberOrVerb, opts)
      })()
    )

  void cite
}

// ── Output ──────────────────────────────────────────────────────────────────

/**
 * Render a single citation as a markdown block. Stable headings so a regex
 * like `^\*\*Doc ID:\*\* (doc-[a-z0-9]+)` reliably extracts the ID; fenced
 * triple-backtick passage so an agent can pull the content with a one-liner.
 * Headers are chalk-bolded when stdout is a TTY — the markdown structure
 * survives in non-TTY output (chalk auto-strips colors).
 */
function renderCitation(r: Resolved): string {
  const firstChunk = r.chunks[0]
  const docTitle = firstChunk?.metadata?.doc_title ?? 'Unknown document'
  const page = firstChunk?.metadata?.page_number
  const docId = firstChunk?.metadata?.doc_ext_id ?? null
  const chunkId = firstChunk?.metadata?.chunk_ext_id ?? null
  const passage = r.chunks.map((c) => c.content).join('\n\n')
  const sha = hashUtf8(passage)

  const lines: string[] = []
  lines.push(chalk.bold(`## Citation ${r.citationNum}`))
  lines.push('')
  lines.push(`${chalk.bold('**Document:**')} ${docTitle}`)
  if (page != null) lines.push(`${chalk.bold('**Page:**')} ${page}`)
  if (docId) lines.push(`${chalk.bold('**Doc ID:**')} ${docId}`)
  if (chunkId) lines.push(`${chalk.bold('**Chunk ID:**')} ${chunkId}`)
  lines.push(`${chalk.bold('**SHA256:**')} ${sha}`)
  lines.push('')
  lines.push(chalk.bold('### Claim'))
  lines.push(r.citationData.statement ?? '')
  lines.push('')
  lines.push(chalk.bold('### Passage'))
  lines.push('```')
  lines.push(passage)
  lines.push('```')
  return lines.join('\n')
}

async function listOrShow(
  number: string | undefined,
  opts: { all?: boolean; json?: boolean }
): Promise<void> {
  const raw = loadLastMetadata()
  if (!raw) {
    if (opts.json) {
      printJson({ count: 0, citations: [] })
      return
    }
    process.stderr.write('No citation data available. Run `arbi ask` first.\n')
    return
  }

  const metadata = raw as MessageMetadataPayload
  if (countCitations(metadata) === 0) {
    if (opts.json) {
      printJson({ count: 0, citations: [] })
      return
    }
    process.stderr.write('The last response contained no citations.\n')
    return
  }

  const resolved = resolveCitations(metadata)

  if (opts.json) {
    const projection = resolved.map((r) => {
      const passage = r.chunks.map((c) => c.content).join('\n\n')
      return {
        num: r.citationNum,
        statement: r.citationData.statement ?? '',
        doc_title: r.chunks[0]?.metadata?.doc_title ?? null,
        page_number: r.chunks[0]?.metadata?.page_number ?? null,
        chunk_ext_ids: r.chunks.map((c) => c.metadata?.chunk_ext_id ?? null),
        doc_ext_ids: [...new Set(r.chunks.map((c) => c.metadata?.doc_ext_id).filter(Boolean))],
        passages: r.chunks.map((c) => c.content),
        passage_hashes: r.chunks.map((c) => hashUtf8(c.content)),
        combined_passage_hash: hashUtf8(passage),
      }
    })
    const filtered = number ? projection.filter((p) => p.num === number) : projection
    printJson({ count: filtered.length, citations: filtered })
    return
  }

  // Default: markdown blocks. List mode (no flags) shows a short index;
  // <N>/--all shows the full markdown for one or every citation.
  if (number) {
    const hit = resolved.find((r) => r.citationNum === number)
    if (!hit) {
      console.error(chalk.red(`Citation [${number}] not found.`))
      process.exit(3)
    }
    console.log(renderCitation(hit))
    return
  }
  if (opts.all) {
    for (let i = 0; i < resolved.length; i++) {
      if (i > 0) console.log('\n---\n')
      console.log(renderCitation(resolved[i]))
    }
    return
  }

  // Index view — one line per citation, but the same parseable shape. An
  // agent that wants the underlying IDs can re-run `arbi cite <N>` for the
  // canonical block.
  console.log(chalk.bold(`# Citations (${resolved.length})`))
  console.log('')
  for (const r of resolved) {
    const docTitle = r.chunks[0]?.metadata?.doc_title ?? 'Unknown document'
    const page = r.chunks[0]?.metadata?.page_number
    const pageSuffix = page != null ? `, p${page}` : ''
    const docId = r.chunks[0]?.metadata?.doc_ext_id
    const statement = (r.citationData.statement ?? '').replace(/\s+/g, ' ').trim()
    const trimmed = statement.length > 140 ? statement.slice(0, 140) + '…' : statement
    console.log(
      `- ${chalk.bold(`[${r.citationNum}]`)} ${docTitle}${pageSuffix}${docId ? ` (${docId})` : ''}`
    )
    console.log(`  ${trimmed}`)
  }
  console.log('')
  console.log(
    chalk.dim('`arbi cite <N>` for the full markdown block; `--json` for the structured payload.')
  )
}

async function verifyOne(number: string, opts: { json?: boolean }): Promise<void> {
  const raw = loadLastMetadata()
  if (!raw) {
    if (opts.json) {
      printJson({ verified: false, reason: 'no-last-metadata' })
      return
    }
    process.stderr.write('No citation data available. Run `arbi ask` first.\n')
    process.exit(1)
  }

  const metadata = raw as MessageMetadataPayload
  const resolved = resolveCitations(metadata)
  const citation = resolved.find((r) => r.citationNum === number)
  if (!citation) {
    if (opts.json) {
      printJson({ verified: false, reason: 'not-found', number })
      return
    }
    console.error(chalk.red(`Citation [${number}] not found in last response.`))
    process.exit(3)
  }

  const chunks = citation.chunks.map((c) => ({
    chunk_ext_id: c.metadata?.chunk_ext_id ?? null,
    doc_ext_id: c.metadata?.doc_ext_id ?? null,
    doc_title: c.metadata?.doc_title ?? null,
    page_number: c.metadata?.page_number ?? null,
    passage_length: c.content.length,
    passage_sha256: hashUtf8(c.content),
  }))

  if (opts.json) {
    printJson({
      verified: chunks.length > 0,
      number,
      statement: citation.citationData.statement ?? '',
      chunks,
      followup_hint:
        'For independent re-verification, run `arbi doc get <doc_ext_id> --json` and confirm the passage hash matches the doc content.',
    })
    return
  }

  // Verify default: a parseable markdown block too. Same `**Doc ID:**` /
  // `**Chunk ID:**` / `**SHA256:**` headers as the cite-show output so the
  // same regex extracts from either.
  console.log(chalk.bold(`## Verification: Citation ${number}`))
  console.log('')
  console.log(`${chalk.bold('**Status:**')} ${chunks.length > 0 ? 'verified' : 'no chunks'}`)
  console.log(`${chalk.bold('**Claim:**')} ${citation.citationData.statement ?? ''}`)
  console.log('')
  for (let i = 0; i < chunks.length; i++) {
    const v = chunks[i]
    console.log(chalk.bold(`### Chunk ${i + 1}/${chunks.length}`))
    console.log(`${chalk.bold('**Doc:**')} ${v.doc_title ?? 'Unknown'}`)
    if (v.page_number != null) console.log(`${chalk.bold('**Page:**')} ${v.page_number}`)
    if (v.doc_ext_id) console.log(`${chalk.bold('**Doc ID:**')} ${v.doc_ext_id}`)
    if (v.chunk_ext_id) console.log(`${chalk.bold('**Chunk ID:**')} ${v.chunk_ext_id}`)
    console.log(`${chalk.bold('**Length:**')} ${v.passage_length} chars`)
    console.log(`${chalk.bold('**SHA256:**')} ${v.passage_sha256}`)
    if (i < chunks.length - 1) console.log('')
  }
  console.log('')
  console.log(
    chalk.dim('Re-verify: `arbi doc get <Doc ID> --json` and compare the passage SHA256.')
  )
}
